import type { Api } from "../../data/api";
import { ApiError } from "../../data/api";
import { t } from "../../lib/sprache";
import { createFromTemplate } from "./vertraege/anlegen";

/**
 * Schnellstart (Aktivierungsplan Vertrieb 16.10.2026, Punkt 1): erster Creator, erste Kooperation, ansehen was daraus wird; oder
 * Beispieldaten (0325). Alles über die Datenbank: Creator über talent_create, Kooperation samt neuer Marke über deal_save_origin,
 * Beispieldaten über sample_data_create (der Vertragsentwurf aus dem Muster entsteht danach wie sonst auch in der App und ist
 * als Teil der Beispiel-Kooperation automatisch ein Beispiel).
 */
export interface SchnellstartStand {
  creators: number;
  deals: number;
  activated: boolean;
  has_sample: boolean;
  created_at: string | null;
  language: string | null;
  talent_id: string | null;
  brand_id: string | null;
  deal_id: string | null;
  period_id: string | null;
  contract_id: string | null;
  invoice_id: string | null;
  review_token: string | null;
  /** erster echter Creator und erste echte Kooperation (für die Anzeige der erledigten Schritte) */
  creator: { id: string; name: string } | null;
  deal: { id: string; title: string; month: string | null } | null;
  /** zur ersten echten Kooperation gibt es schon einen Vertrag bzw. eine Rechnung */
  dealContract: string | null;
  dealInvoice: string | null;
}

const parse = <T,>(v: T | string): T => (typeof v === "string" ? (JSON.parse(v) as T) : v);

export async function loadSchnellstart(api: Api, orgId: string): Promise<SchnellstartStand> {
  const status = parse(await api.read<Omit<SchnellstartStand, "creator" | "deal" | "dealContract" | "dealInvoice"> | string>("sample_data_status", { p_org: orgId }));
  const [talents, deals] = await Promise.all([
    api.select<{ id: string; first_name: string; last_name: string | null; is_sample?: boolean; is_self?: boolean | null }>("talents", {
      columns: "id, first_name, last_name, is_sample, is_self", filters: [["org_id", "eq", orgId], ["is_sample", "is", false], ["status", "neq", "archived"]], order: [["created_at", "asc"]], limit: 5,
    }),
    api.select<{ id: string; title: string; start_month: string | null }>("deals", {
      columns: "id, title, start_month", filters: [["org_id", "eq", orgId], ["is_sample", "is", false]], order: [["created_at", "asc"]], limit: 1,
    }),
  ]);
  const creator = talents.find((x) => !x.is_self) ?? null;
  const deal = deals[0] ?? null;
  const [contracts, invoices] = deal
    ? await Promise.all([
      api.select<{ id: string }>("contracts", { columns: "id", filters: [["deal_id", "eq", deal.id]], limit: 1 }).catch(() => []),
      api.select<{ id: string }>("invoices", { columns: "id", filters: [["deal_id", "eq", deal.id]], limit: 1 }).catch(() => []),
    ])
    : [[], []];
  return {
    ...status,
    creator: creator ? { id: creator.id, name: [creator.first_name, creator.last_name].filter(Boolean).join(" ") } : null,
    deal: deal ? { id: deal.id, title: deal.title, month: deal.start_month } : null,
    dealContract: contracts[0]?.id ?? null,
    dealInvoice: invoices[0]?.id ?? null,
  };
}

/** Instagram-Handle wie in der Datenbank erlaubt (ohne @, Buchstaben, Ziffern, Punkt, Unterstrich, bis 30 Zeichen) */
export function handleAus(eingabe: string): string | null {
  const h = eingabe.trim().replace(/^https?:\/\/(www\.)?instagram\.com\//i, "").replace(/^@/, "").replace(/\/.*$/, "");
  return /^[A-Za-z0-9._]{1,30}$/.test(h) ? h : null;
}

/** „Vorname Nachname“ → Vor- und Nachname (alles nach dem ersten Wort ist der Nachname) */
export function nameTeile(name: string): { first: string; last: string | null } {
  const teile = name.trim().split(/\s+/).filter(Boolean);
  return { first: teile[0] ?? "", last: teile.slice(1).join(" ") || null };
}

export interface CreatorEingabe { name: string; instagram: string }
export function pruefeCreator(e: CreatorEingabe): Partial<Record<keyof CreatorEingabe, string>> {
  const f: Partial<Record<keyof CreatorEingabe, string>> = {};
  if (nameTeile(e.name).first.length < 1) f.name = t("Bitte den Namen eintragen.", "Please enter the name.");
  // K-558 c: wie „Creator anlegen“ und Coco reicht der Name; ein eingetragener Handle muss aber stimmen
  if (e.instagram.trim() && !handleAus(e.instagram)) f.instagram = t("Der Instagram-Handle sieht nicht richtig aus, zum Beispiel @mila.beispiel.", "The Instagram handle does not look right, for example @mila.sample.");
  return f;
}

/** Schritt 1: Name (Instagram-Handle freiwillig), alles andere später im Profil */
export async function legeCreatorAn(api: Api, orgId: string, e: CreatorEingabe, country: string | null): Promise<string> {
  const { first, last } = nameTeile(e.name);
  const r = parse(await api.rpc<{ id: string } | string>("talent_create", {
    p_org: orgId, p_values: { first_name: first, last_name: last, instagram_handle: handleAus(e.instagram), ...(country ? { country } : {}), status: "active" }, p_allow_duplicate: true,
  }));
  return r.id;
}

export interface KoopEingabe { talentId: string; marke: string; markeId: string | null; betrag: string; von: string; bis: string; titel: string }

/** Monatsliste von „YYYY-MM“ bis „YYYY-MM“ (höchstens 36 Monate) */
export function monate(von: string, bis: string): string[] {
  if (!/^\d{4}-\d{2}$/.test(von) || !/^\d{4}-\d{2}$/.test(bis)) return [];
  const out: string[] = [];
  let [y, m] = von.split("-").map(Number) as [number, number];
  const [ye, me] = bis.split("-").map(Number) as [number, number];
  while ((y < ye || (y === ye && m <= me)) && out.length < 36) {
    out.push(`${y}-${String(m).padStart(2, "0")}`);
    m += 1;
    if (m > 12) { m = 1; y += 1; }
  }
  return out;
}

/** Betrag „1.500“, „1500,50“, „1,500.50“ → Cent; ungültig: null */
export function centAus(eingabe: string): number | null {
  let s = eingabe.trim().replace(/\s|€|EUR|CHF/gi, "");
  if (!s) return null;
  if (/,\d{1,2}$/.test(s)) s = s.replace(/\./g, "").replace(",", ".");
  else s = s.replace(/,/g, "");
  if (/^\d+\.\d{3}$/.test(s)) s = s.replace(".", "");
  const n = Number(s);
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : null;
}

export function pruefeKoop(e: KoopEingabe): Partial<Record<keyof KoopEingabe, string>> {
  const f: Partial<Record<keyof KoopEingabe, string>> = {};
  if (!e.talentId) f.talentId = t("Bitte den Creator wählen.", "Please choose the creator.");
  if (!e.markeId && e.marke.trim().length < 1) f.marke = t("Bitte den Namen der Marke eintragen.", "Please enter the brand's name.");
  if (centAus(e.betrag) === null) f.betrag = t("Bitte einen Betrag eintragen, zum Beispiel 1.500.", "Please enter an amount, for example 1,500.");
  if (monate(e.von, e.bis).length === 0) f.bis = t("Bitte einen Zeitraum wählen: das Ende liegt nicht vor dem Beginn.", "Please choose a period: the end is not before the start.");
  return f;
}

/** Schritt 2: Kooperation mit Marke (neu per Name oder vorhanden), Betrag je Monat und Zeitraum */
export async function legeKooperationAn(api: Api, orgId: string, e: KoopEingabe, currency: string, creatorName: string): Promise<string> {
  const liste = monate(e.von, e.bis);
  const cents = centAus(e.betrag)!;
  const marke = e.marke.trim();
  const titel = e.titel.trim() || `${marke || t("Kooperation", "Collaboration")} × ${creatorName}`.slice(0, 200);
  const r = parse(await api.rpc<{ deal_id: string } | string>("deal_save_origin", {
    p_origin: "agency", p_deal: null,
    p_values: { org_id: orgId, talent_id: e.talentId, ...(e.markeId ? { brand_id: e.markeId } : {}), title: titel, model: liste.length > 1 ? "monthly" : "fixed", currency },
    p_terms: null,
    p_months: liste.map((month) => ({ month, amount_cents: cents })),
    p_items: [],
    p_new_brand: e.markeId ? null : marke,
    p_new_campaign: null,
    p_expected_updated_at: null,
  }));
  if (!r?.deal_id) throw new ApiError(t("Die Kooperation wurde nicht angelegt.", "The collaboration was not created."));
  return r.deal_id;
}

/** Vertragsentwurf aus dem Muster „Kooperationsvertrag“ in der gewünschten Sprache (nichts wird versendet) */
export async function vertragsentwurfAusMuster(api: Api, orgId: string, input: { dealId: string; orgName: string; language: "de" | "en"; today: string }): Promise<string | null> {
  const tpls = await api.select<{ id: string; key: string | null; language: string; name: string; status: string }>("contract_templates", {
    columns: "id, key, language, name, status", filters: [["org_id", "eq", orgId], ["key", "eq", "cooperation"]],
  });
  const tpl = tpls.find((x) => x.language === input.language && x.status === "active") ?? tpls.find((x) => x.language === input.language) ?? tpls[0];
  if (!tpl) return null;
  const titel = `${tpl.name.replace(/\s*\((Muster|sample|Sample)\)\s*$/, "")}`;
  return createFromTemplate(api, orgId, { templateId: tpl.id, title: titel, talentId: null, brandId: null, dealId: input.dealId, orgName: input.orgName, today: input.today });
}

/** Beispieldaten anlegen; der Beispielvertrag kommt danach aus dem Muster (scheitert er, bleiben die übrigen Beispieldaten) */
export async function legeBeispieldatenAn(api: Api, orgId: string, input: { orgName: string; language: "de" | "en"; today: string }): Promise<SchnellstartStand> {
  const s = parse(await api.rpc<{ deal_id: string | null; contract_id: string | null } | string>("sample_data_create", { p_org: orgId, p_language: input.language }));
  if (s.deal_id && !s.contract_id) {
    await vertragsentwurfAusMuster(api, orgId, { dealId: s.deal_id, orgName: input.orgName, language: input.language, today: input.today }).catch(() => null);
  }
  return loadSchnellstart(api, orgId);
}

export async function loescheBeispieldaten(api: Api, orgId: string): Promise<void> {
  await api.rpc("sample_data_delete", { p_org: orgId });
}

/** Weiche der Übersicht: noch nichts Eigenes angelegt und keine Beispieldaten → Schnellstart statt leerer Übersicht */
export function zumSchnellstart(stand: { has_sample: boolean; creators: number; deals: number } | null | undefined, spaeter: boolean): boolean {
  return !!stand && !spaeter && !stand.has_sample && stand.creators === 0 && stand.deals === 0;
}

// Schritt 3 „angesehen“ und „Schnellstart später“ je Agentur in diesem Browser
const key = (orgId: string, was: string) => `tk-schnellstart-${was}-${orgId}`;
export function merke(orgId: string, was: "angesehen" | "spaeter" | "beispiel-frage", on = true) {
  try {
    if (on) localStorage.setItem(key(orgId, was), "1");
    else localStorage.removeItem(key(orgId, was));
  } catch {
    /* ohne Speicher: nur für diese Ansicht */
  }
}
export function gemerkt(orgId: string, was: "angesehen" | "spaeter" | "beispiel-frage"): boolean {
  try {
    return localStorage.getItem(key(orgId, was)) === "1";
  } catch {
    return false;
  }
}
